import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { Grid, Box, P, H2, Text } from '@appcues/component-library';
import { ExternalLink } from 'next/components/ui';

import {
  selectIntegrations,
  selectAreIntegrationsSynced,
} from 'reducers/account/integrations';

import {
  selectAvailableIntegrationTypes,
  selectAreIntegrationTypesSynced,
} from 'reducers/integrationTypes';
import Loader from 'components/Common/Loader';
import IntegrationCard, { IntegrationCardWrapper } from './IntegrationCard';
import IntegrationBeacon from './IntegrationBeacon';

export const IntegrationsPage = ({
  accountIntegrations,
  integrationTypes,
  isSynced,
}) => {
  if (!isSynced) {
    return <Loader />;
  }

  // Do not display Hubspot one way if they are not enabled.
  // New integrations should just use Hubspot Two-Way
  const premiumIntegrations = integrationTypes
    .filter(integration => integration.premium)
    .map(integration => ({
      ...integration,
      isEnabled: accountIntegrations[integration.id]?.isEnabled,
    }))
    .filter(
      integration => !(integration.id === 'hubspot' && !integration.isEnabled)
    );

  const totalPremium = premiumIntegrations.length;
  const totalAvailable = premiumIntegrations.filter(
    integration => integration.available
  ).length;

  return (
    <>
      <H2 type="secondary">Integrations</H2>
      <P marginTop={4} type="secondary">
        Track how users interact with your content in the tools you already use.{' '}
        <ExternalLink href="https://docs.appcues.com/article/137-integrations-overview">
          Learn more about integrations
        </ExternalLink>
      </P>
      <Grid minItemWidth={225} marginTop={20} marginBottom={60}>
        {integrationTypes
          .filter(integration => !integration.premium)
          .map(integrationType => (
            <IntegrationCard
              key={integrationType.id}
              integration={integrationType}
              isEnabled={accountIntegrations[integrationType.id]?.isEnabled}
            />
          ))}
        <Box
          is="a"
          textDecoration="none"
          href="https://appcues.typeform.com/to/ADzHU0"
          target="_blank"
        >
          <IntegrationCardWrapper padding="20px 36px" available>
            <div>
              <Text type="secondary" is="div">
                <strong>Integration request?</strong>
              </Text>
              <Text type="secondary" is="div">
                Let us know
              </Text>
            </div>
          </IntegrationCardWrapper>
        </Box>
      </Grid>
      {totalPremium > 0 && (
        <>
          <H2 type="secondary">Premium Integrations</H2>
          <P marginTop={4} type="secondary">
            Integrations available to Growth and Enterprise customers only.{' '}
            {totalAvailable < totalPremium && (
              <IntegrationBeacon>Contact us to upgrade</IntegrationBeacon>
            )}
          </P>
          <Grid minItemWidth={225} marginTop={20} marginBottom={40}>
            {premiumIntegrations.map(integrationType => (
              <IntegrationCard
                key={integrationType.id}
                integration={integrationType}
                isEnabled={integrationType.isEnabled}
              />
            ))}
          </Grid>
        </>
      )}
    </>
  );
};

IntegrationsPage.propTypes = {
  integrationTypes: PropTypes.arrayOf(
    PropTypes.shape({
      available: PropTypes.bool,
      base64Image: PropTypes.string,
      description: PropTypes.string,
      name: PropTypes.string,
      id: PropTypes.string,
      imageUrl: PropTypes.string,
    })
  ),
  isSynced: PropTypes.bool,
  accountIntegrations: PropTypes.object,
};

IntegrationsPage.defaultProps = {
  isSynced: false,
  accountIntegrations: {},
  integrationTypes: [],
};

const mapStateToProps = state => ({
  accountIntegrations: selectIntegrations(state),
  integrationTypes: selectAvailableIntegrationTypes(state),
  isSynced:
    selectAreIntegrationTypesSynced(state) &&
    selectAreIntegrationsSynced(state),
});
export default connect(mapStateToProps)(IntegrationsPage);
